import {
    autoUpdate,
    FloatingNode,
    FloatingTree,
    type Placement,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useInteractions,
    useMergeRefs,
    useRole,
} from '@floating-ui/react'
import { useReducedMotion } from 'motion/react'
import { cloneElement, isValidElement, type MouseEvent, type ReactNode, type Ref, useState } from 'react'

import { dropdownMiddleware, useDropdownList, useStayWhereOpened } from './dropdownFloating'
import { DropdownPanel, type DropdownPanelProps, resolvePanelRole } from './DropdownPanel'
import type { DropdownMenuSearch } from './DropdownSearch'

export type AstraDropdownMenuProps = DropdownPanelProps & {
    /**
     * Content rendered inside the floating dropdown panel, usually AstraDropdownItem items.
     */
    dropdownContent: ReactNode
    /**
     * The trigger element that opens the menu. It must be a valid React element so menu handlers and refs can be attached.
     */
    children: ReactNode
    /**
     * If `true`, the trigger cannot open the menu.
     */
    disabled?: boolean
    /**
     * Called whenever the menu requests an open state change. Use with `open` for controlled state.
     */
    onOpenChange?: (open: boolean) => void
    /**
     * Controlled open state. Omit this prop to let the component manage open state internally.
     */
    open?: boolean
    /**
     * Floating UI placement for the menu relative to the trigger, such as `bottom`, `bottom-start`, or `right-end`.
     */
    placement?: Placement
    /**
     * Distance in pixels between the trigger and the menu along the placement axis. Defaults to `4`.
     */
    sideOffset?: number
    /**
     * Offset along the alignment axis, used to nudge start/end aligned menus.
     */
    alignOffset?: number
    /**
     * If `true`, flips and shifts the menu to keep it inside the viewport.
     */
    avoidCollisions?: boolean
    /**
     * Viewport padding used when collision handling is enabled.
     */
    collisionPadding?: number
    /**
     * If `true`, keyboard navigation wraps from the last item to the first item and back.
     */
    loop?: boolean
    /**
     * If `true`, focus is trapped inside the menu while it is open.
     */
    modal?: boolean
    /**
     * A search field pinned above the items, for a list too long to read through. The panel then
     * scrolls, capped at a default height that `className` overrides.
     */
    search?: DropdownMenuSearch
    /**
     * Keep the panel where it opened, rather than following its trigger. For a trigger that what is chosen
     * in the panel moves — a button at the end of a row of chips, pushed along by every chip added — where
     * following it would slide the panel out from under the pointer mid-selection.
     */
    stayWhereOpened?: boolean
}

type TriggerElementProps = {
    ref?: Ref<HTMLElement>
    disabled?: boolean
    onClick?: (event: MouseEvent<Element>) => void
    [key: string]: unknown
}

const AstraDropdownMenuImpl = ({
    dropdownContent,
    children,
    disabled,
    onOpenChange,
    open,
    placement = 'bottom',
    sideOffset,
    alignOffset,
    avoidCollisions,
    collisionPadding,
    loop = true,
    modal = false,
    reducedMotion,
    search,
    role = 'menu',
    stayWhereOpened = false,
    ...contentProps
}: AstraDropdownMenuProps) => {
    const isControlled = open !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const currentOpen = isControlled ? open : uncontrolledOpen

    const setCurrentOpen = (nextOpen: boolean) => {
        if (!isControlled) setUncontrolledOpen(nextOpen)
        onOpenChange?.(nextOpen)
    }

    const prefersReducedMotion = useReducedMotion()
    const skipMotion = reducedMotion ?? prefersReducedMotion ?? false

    const nodeId = useFloatingNodeId()

    const {
        x,
        y,
        strategy,
        refs,
        context,
        placement: resolvedPlacement,
    } = useFloating({
        nodeId,
        open: currentOpen,
        onOpenChange: setCurrentOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: dropdownMiddleware({ sideOffset, alignOffset, avoidCollisions, collisionPadding }),
    })

    useStayWhereOpened(refs, { enabled: stayWhereOpened, open: currentOpen })

    const click = useClick(context, { enabled: !disabled })
    const dismiss = useDismiss(context)
    const roleInteraction = useRole(context, { role: resolvePanelRole(search, role) })
    const { list, interactions } = useDropdownList(context, { search, loop })
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, roleInteraction, ...interactions])

    // Preserve any ref the consumer attached to the trigger; cloneElement would otherwise drop it in favor of setReference.
    const triggerRef = isValidElement<TriggerElementProps>(children) ? children.props.ref : undefined
    const setTriggerRef = useMergeRefs<HTMLElement>([refs.setReference, triggerRef])

    if (!isValidElement<TriggerElementProps>(children)) return <>{children}</>

    const triggerProps = children.props
    const referenceProps = getReferenceProps({
        ...triggerProps,
        ...(disabled ? { 'aria-disabled': true } : {}),
        onClick: event => {
            if (disabled) {
                event.preventDefault()
                return
            }
            triggerProps.onClick?.(event)
        },
    })

    return (
        <FloatingNode id={nodeId}>
            {cloneElement(children, {
                ...referenceProps,
                disabled: triggerProps.disabled || disabled,
                ref: setTriggerRef,
            })}
            <DropdownPanel
                {...contentProps}
                open={currentOpen}
                floating={{
                    context,
                    setFloating: refs.setFloating,
                    placement: resolvedPlacement,
                    x,
                    y,
                    strategy,
                    getFloatingProps,
                    getItemProps,
                }}
                list={list}
                search={search}
                skipMotion={skipMotion}
                closeMenu={() => setCurrentOpen(false)}
                modal={modal}
                role={role}
            >
                {dropdownContent}
            </DropdownPanel>
        </FloatingNode>
    )
}

/**
 * Opens a list of actions from a trigger. Submenus coordinate through a FloatingTree, so the root menu
 * provides one; a menu rendered inside another menu joins the tree already there rather than starting
 * a second one.
 */
export const AstraDropdownMenu = (props: AstraDropdownMenuProps) => {
    const parentId = useFloatingParentNodeId()

    if (parentId === null) {
        return (
            <FloatingTree>
                <AstraDropdownMenuImpl {...props} />
            </FloatingTree>
        )
    }

    return <AstraDropdownMenuImpl {...props} />
}
